/**
 * Prompt detail. Copy is the single primary action and lives on Telegram's BottomButton;
 * save and share sit in an in-page secondary row, matching the design.
 *
 * Rating and favourite are optimistic — the star fills and the heart flips on tap, with a
 * rollback if the request fails, so a slow network never makes a tap feel lost.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { CommentNode, PromptCard as PromptCardDto, PromptDetail } from '@promptvault/shared';
import {
  downloadPrompt,
  fetchComments,
  fetchFeed,
  fetchPrompt,
  likeComment,
  postComment,
  ratePrompt,
  toggleFavorite,
} from '../api/prompts';
import { CollectionSheet } from '../components/CollectionSheet';
import { Gallery } from '../components/Gallery';
import { Icon } from '../components/Icon';
import { PromptCard } from '../components/PromptCard';
import { Padded, Screen } from '../components/Screen';
import { useToast } from '../components/Toast';
import {
  CommentComposer,
  CommentItem,
  DisclosureRow,
  PromptTextBlock,
  Rail,
  SectionTitle,
} from '../components/content';
import { ModelBadge, RatingInput, TagPill } from '../components/controls';
import { InternalStatsBlock, SourceStatsBlock } from '../components/SourceStats';
import { ErrorState, SkeletonComments, SkeletonDetail } from '../components/feedback';
import { Avatar, Cell } from '../components/primitives';
import { compactNumber } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { haptic, hapticNotify, useMainButton } from '../hooks/useTg';
import { openLink, openTelegramLink, tokenColor } from '../lib/telegram';

const BOT_USERNAME = 'PromptVaultAIBot';

export function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { t, lang } = useI18n();

  const [copied, setCopied] = useState(false);
  const [collectionSheet, setCollectionSheet] = useState(false);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<CommentNode | null>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  const promptQuery = useQuery({
    queryKey: ['prompt', id],
    queryFn: () => fetchPrompt(id!),
    enabled: Boolean(id),
  });
  const prompt = promptQuery.data;

  const commentsQuery = useQuery({
    queryKey: ['comments', id],
    queryFn: () => fetchComments(id!),
    enabled: Boolean(id),
  });

  const relatedQuery = useQuery({
    queryKey: ['related', prompt?.type, id],
    queryFn: () => fetchFeed({ type: prompt!.type, sort: 'trending', limit: 8 }),
    enabled: Boolean(prompt),
    staleTime: 5 * 60_000,
  });

  const related = useMemo(
    () => (relatedQuery.data?.items ?? []).filter((c) => c.id !== id).slice(0, 6),
    [relatedQuery.data, id],
  );

  /** Patch the cached detail in place — the basis of every optimistic interaction here. */
  const patchPrompt = useCallback(
    (patch: Partial<PromptDetail>) => {
      qc.setQueryData<PromptDetail>(['prompt', id], (prev) =>
        prev ? { ...prev, ...patch } : prev,
      );
    },
    [qc, id],
  );

  const copyMut = useMutation({
    mutationFn: () => downloadPrompt(id!),
    onSuccess: async (data) => {
      try {
        await navigator.clipboard.writeText(data.body);
      } catch {
        // Clipboard can be blocked (no user gesture, insecure context). Fall back to a
        // hidden selection rather than reporting a success that never happened.
        const ta = document.createElement('textarea');
        ta.value = data.body;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
        } catch {
          /* nothing further we can do */
        }
        document.body.removeChild(ta);
      }
      hapticNotify('success');
      setCopied(true);
      toast(t.copied);
      patchPrompt({ downloadCount: (prompt?.downloadCount ?? 0) + 1 });
      window.setTimeout(() => setCopied(false), 1700);
    },
    onError: () => {
      hapticNotify('error');
      toast(t.errorTitle, 'error');
    },
  });

  const rateMut = useMutation({
    mutationFn: (stars: number) => ratePrompt(id!, stars),
    onMutate: (stars) => {
      const previous = qc.getQueryData<PromptDetail>(['prompt', id]);
      patchPrompt({ myRating: stars });
      return { previous };
    },
    onSuccess: (res) => {
      hapticNotify('success');
      patchPrompt({
        ratingAvg: res.ratingAvg,
        ratingCount: res.ratingCount,
        myRating: res.myRating,
      });
      toast(t.ratingSaved);
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['prompt', id], ctx.previous);
      hapticNotify('error');
      toast(t.errorTitle, 'error');
    },
  });

  const favMut = useMutation({
    mutationFn: () => toggleFavorite(id!),
    onMutate: () => {
      const previous = qc.getQueryData<PromptDetail>(['prompt', id]);
      patchPrompt({
        isFavorited: !previous?.isFavorited,
        favoriteCount: (previous?.favoriteCount ?? 0) + (previous?.isFavorited ? -1 : 1),
      });
      return { previous };
    },
    onSuccess: () => {
      haptic('medium');
      void qc.invalidateQueries({ queryKey: ['favorites'] });
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(['prompt', id], ctx.previous);
      hapticNotify('error');
    },
  });

  const commentMut = useMutation({
    mutationFn: (body: string) => postComment(id!, body, replyTo?.id),
    onSuccess: () => {
      setDraft('');
      setReplyTo(null);
      hapticNotify('success');
      void qc.invalidateQueries({ queryKey: ['comments', id] });
      patchPrompt({ commentCount: (prompt?.commentCount ?? 0) + 1 });
    },
    onError: () => {
      hapticNotify('error');
      toast(t.errorTitle, 'error');
    },
  });

  const likeMut = useMutation({
    mutationFn: (commentId: string) => likeComment(commentId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['comments', id] }),
  });

  const handleCopy = useCallback(() => {
    haptic('medium');
    copyMut.mutate();
  }, [copyMut]);

  useMainButton({
    text: copied ? `✓ ${t.copiedBtn}` : t.copyPrompt,
    onClick: handleCopy,
    visible: Boolean(prompt),
    progress: copyMut.isPending,
    color: copied ? tokenColor('--pv-success') : undefined,
    textColor: copied ? tokenColor('--pv-on-media') : undefined,
  });

  // A deep link can land straight here; make sure the view starts at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleShare = useCallback(() => {
    if (!prompt) return;
    haptic('light');
    // `t.me/<bot>?startapp=` — the Main Mini App. The `/app` short name this used to name was
    // never issued by BotFather (`/myapps`: "You currently have no web apps"), so every share
    // sent a link that resolved to nothing.
    const url = `https://t.me/${BOT_USERNAME}?startapp=prompt_${prompt.id}`;
    openTelegramLink(
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(prompt.title)}`,
    );
  }, [prompt]);

  const openCard = useCallback((card: PromptCardDto) => navigate(`/prompt/${card.id}`), [navigate]);

  if (promptQuery.isLoading) {
    return (
      <Screen hasBottomButton>
        <SkeletonDetail />
      </Screen>
    );
  }

  if (promptQuery.isError || !prompt) {
    return (
      <Screen>
        <ErrorState
          message={(promptQuery.error as Error)?.message}
          onRetry={() => void promptQuery.refetch()}
        />
      </Screen>
    );
  }

  const comments: CommentNode[] = commentsQuery.data?.items ?? [];

  return (
    <Screen hasBottomButton chrome="surface">
      {/* NSFW media is gated on the prompt's own flag, not on the viewer's setting: a deep
          link can reach this screen without ever passing the feed's server-side filter. */}
      <Gallery
        seed={prompt.id}
        examples={prompt.examples}
        alt={prompt.title}
        blurNsfw={prompt.isNsfw}
      />

      {/* Title block sits on surface so it reads as one plate with the media above it. */}
      <div className="flex flex-col gap-2.5 bg-surface px-gutter pb-4 pt-3.5">
        <h1 className="bidi-plaintext text-balance text-title font-semibold text-text">
          {prompt.title}
        </h1>
        {prompt.models.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {prompt.models.map((m) => (
              <ModelBadge key={m.id} name={m.name} />
            ))}
          </div>
        )}
        {/*
          Was a single `StatRow` printing `★ 0.0 (0) · ⬇ 0` on every prompt in the library. Two
          sections now, because the entire point is that one set of numbers is not ours: a reader
          who cannot tell our 1 download from Civitai's 2,913 reactions has been given a
          misleading impression rather than a dense one. The PromptVault block hides itself
          entirely until it has something to say.
        */}
        <SourceStatsBlock engagement={prompt.sourceEngagement} />
        <InternalStatsBlock
          ratingAvg={prompt.ratingAvg}
          ratingCount={prompt.ratingCount}
          downloadCount={prompt.downloadCount}
          favoriteCount={prompt.favoriteCount}
          viewCount={prompt.viewCount}
        />
      </div>

      {/* Secondary actions — the primary one is Telegram's BottomButton. */}
      <Padded className="pt-3">
        <div className="flex gap-2">
          <button
            type="button"
            aria-pressed={prompt.isFavorited}
            onClick={() => {
              haptic('medium');
              favMut.mutate();
            }}
            className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-button bg-surface text-callout font-medium transition-transform duration-fast ease-pv active:scale-[0.98] ${
              prompt.isFavorited ? 'text-destructive' : 'text-accent'
            }`}
          >
            <Icon
              name="heart"
              size={18}
              fill={prompt.isFavorited}
              className={prompt.isFavorited ? 'pv-pop' : ''}
            />
            {prompt.isFavorited ? t.saved : t.save}
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="flex h-11 flex-1 items-center justify-center gap-2 rounded-button bg-surface text-callout font-medium text-accent transition-transform duration-fast ease-pv active:scale-[0.98]"
          >
            <Icon name="share" size={18} />
            {t.share}
          </button>
        </div>
      </Padded>

      {/*
        Add to collection — its own row, not folded into Save.

        Save is the one-tap favourite and stays one tap. Choosing a collection is a decision, and
        a decision belongs behind a sheet. The endpoints and the client for this have existed
        since collections shipped, with no caller: a member could create a collection and then had
        no way to put anything in it.
      */}
      <Padded className="pt-2">
        <div className="overflow-hidden rounded-card bg-surface">
          <Cell
            icon="plus"
            title={t.addToCollection}
            chevron
            last
            onClick={() => setCollectionSheet(true)}
          />
        </div>
      </Padded>

      <Padded className="pt-3">
        <PromptTextBlock
          label={t.prompt}
          body={prompt.body}
          mono={prompt.type === 'code'}
          onCopy={handleCopy}
        />
      </Padded>

      {prompt.negativePrompt && (
        <Padded className="pt-2">
          <DisclosureRow label={t.negPrompt}>{prompt.negativePrompt}</DisclosureRow>
        </Padded>
      )}

      {prompt.instructions && (
        <Padded className="pt-2">
          <DisclosureRow label={t.howToUse}>{prompt.instructions}</DisclosureRow>
        </Padded>
      )}

      {prompt.tags.length > 0 && (
        <Padded className="pt-4">
          <div className="flex flex-wrap gap-1.5">
            {prompt.tags.map((tag) => (
              <TagPill
                key={tag}
                tag={tag}
                onClick={() => navigate(`/search?tag=${encodeURIComponent(tag)}`)}
              />
            ))}
          </div>
        </Padded>
      )}

      {/* Attribution: members link to their author screen, ingested sources to the origin URL. */}
      {(prompt.author || prompt.sourceName) && (
        <Padded className="pt-4">
          <button
            type="button"
            onClick={() => {
              haptic('light');
              if (prompt.author) navigate(`/author/${encodeURIComponent(prompt.author.key)}`);
              // `openLink`, not `window.open`. Inside Telegram's webview a popup opened without a
              // user-gesture chain is routinely blocked and returns null, so the source link did
              // nothing at all on the client this app is written for. `openLink` hands the URL to
              // Telegram, and falls back to `window.open` outside it. It existed here with zero
              // callers the whole time.
              else if (prompt.sourceUrl) openLink(prompt.sourceUrl);
            }}
            className="flex w-full items-center gap-3 rounded-card bg-surface px-3.5 py-3 text-start transition-colors duration-fast ease-pv active:bg-pressed-soft"
          >
            <Avatar
              name={prompt.author?.name ?? prompt.sourceName ?? '?'}
              photoUrl={prompt.author?.photoUrl}
              size={38}
            />
            <span className="min-w-0 flex-1">
              <span className="bidi-plaintext block truncate text-body text-text">
                {prompt.author?.name ?? prompt.sourceName}
              </span>
              <span className="block truncate text-footnote text-hint">
                {prompt.author?.isMember ? t.memberMeta : (prompt.sourceName ?? t.sourceMeta)}
              </span>
            </span>
            <Icon name="chevron" size={19} className="flex-none text-chevron" />
          </button>
        </Padded>
      )}

      <Padded className="pt-4">
        <div className="flex flex-col items-start gap-2 rounded-card bg-surface p-3.5">
          <span className="text-headline font-semibold text-text">{t.rateThis}</span>
          <RatingInput
            value={prompt.myRating ?? 0}
            onRate={(stars) => rateMut.mutate(stars)}
            disabled={rateMut.isPending}
          />
          {/* Flex, not string concatenation: a bare "·" between RTL words and a numeral is
              bidi-neutral and gets reordered into the number. */}
          {prompt.myRating ? (
            <span className="flex items-baseline gap-1.5 text-footnote text-hint">
              <span>{t.yourRating}</span>
              <span aria-hidden="true" className="opacity-45">
                ·
              </span>
              <span className="tnum">
                {compactNumber(prompt.myRating, lang)}/{compactNumber(5, lang)}
              </span>
            </span>
          ) : (
            <span className="text-footnote text-hint">{t.tapToRate}</span>
          )}
        </div>
      </Padded>

      <Padded className="pt-5">
        {/* Separate nodes, not a template string: a bare "·" between RTL text and a digit is
            bidi-neutral and reorders into the number ("نظرات ۳۰"). */}
        <SectionTitle
          title={
            <span className="flex items-baseline gap-1.5">
              <span>{t.comments}</span>
              <span aria-hidden="true" className="opacity-45">
                ·
              </span>
              <span className="tnum">{compactNumber(prompt.commentCount, lang)}</span>
            </span>
          }
        />
      </Padded>

      <Padded className="pt-2.5">
        {commentsQuery.isLoading ? (
          <SkeletonComments />
        ) : comments.length > 0 ? (
          <div className="flex flex-col gap-2">
            {comments.map((c) => (
              <CommentItem
                key={c.id}
                comment={c}
                onLike={(x) => likeMut.mutate(x.id)}
                onReply={(x) => {
                  setReplyTo(x);
                  composerRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                }}
              />
            ))}
          </div>
        ) : (
          <p className="py-4 text-center text-subhead text-hint">{t.noComments}</p>
        )}
      </Padded>

      <Padded className="pt-3">
        <div ref={composerRef}>
          {replyTo && (
            <div className="mb-2 flex items-center gap-2 rounded-card bg-fill px-3 py-2">
              <Icon name="reply" size={14} className="flex-none text-hint" />
              <span className="min-w-0 flex-1 truncate text-footnote text-hint">
                {t.reply} · <bdi className="text-text">{replyTo.author.name}</bdi>
              </span>
              <button
                type="button"
                aria-label={t.reset}
                onClick={() => setReplyTo(null)}
                className="flex-none text-hint"
              >
                <Icon name="close" size={14} strokeWidth={3} />
              </button>
            </div>
          )}
          <CommentComposer
            value={draft}
            onChange={setDraft}
            onSend={() => commentMut.mutate(draft.trim())}
            placeholder={replyTo ? `${t.reply} ${replyTo.author.name}…` : t.writeComment}
            disabled={commentMut.isPending}
          />
        </div>
      </Padded>

      {related.length > 0 && (
        <div className="pt-6">
          <Padded>
            <SectionTitle title={t.related} />
          </Padded>
          <div className="px-gutter pt-2.5">
            <Rail itemWidth={112}>
              {related.map((card) => (
                <PromptCard key={card.id} card={card} variant="mini" onOpen={openCard} />
              ))}
            </Rail>
          </div>
        </div>
      )}

      <div className="h-6" />

      <CollectionSheet
        open={collectionSheet}
        onClose={() => setCollectionSheet(false)}
        promptId={prompt.id}
      />
    </Screen>
  );
}
